import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { ANONYMOUS, fetchMe, login, logout, register, type Me } from '../api/auth';
import { OfflineError } from '../api/http';
import { accountOf, forgetAll, noteAccount, noteSignedOut } from '../api/storedReads';

interface AuthState {
  me: Me;
  loaded: boolean;
  /**
   * `/api/auth/me` found no platform and the device keeps no copy of it: who
   * is signed in is not known, which is not the same as nobody (`AuthGate`).
   */
  offline: boolean;
  refresh: () => Promise<void>;
  signIn: (handle: string, password: string, remember: boolean) => Promise<Me>;
  signOut: () => Promise<void>;
  signUp: (input: {
    handle: string;
    displayName: string;
    password: string;
    inviteCode: string;
  }) => Promise<void>;
}

const AuthContext = createContext<AuthState>({
  me: ANONYMOUS,
  loaded: false,
  offline: false,
  refresh: async () => {},
  signIn: async () => ANONYMOUS,
  signOut: async () => {},
  signUp: async () => {},
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me>(ANONYMOUS);
  const [loaded, setLoaded] = useState(false);
  const [offline, setOffline] = useState(false);

  /**
   * Bumped by every sign-in, sign-up and sign-out. A /me answer is only kept
   * when nothing changed the account since it was asked: one sent before a
   * sign-out (React's doubled mount effect in development sends two) and
   * answered after it put the account back, and "Surt" landed on the home
   * page signed in (the phone e2e on MySQL, 2026-09-28).
   */
  const generation = useRef(0);

  const refresh = useCallback(async () => {
    const asked = generation.current;
    try {
      const fresh = await fetchMe();
      if (asked === generation.current) {
        setMe(fresh);
        setOffline(false);
        if (fresh.authenticated) noteSignedOut(false);
        // Whose reads this device keeps from now on (`storedReads.ts`).
        await noteAccount(accountOf(fresh));
      }
    } catch (err) {
      if (asked === generation.current) {
        setMe(ANONYMOUS);
        setOffline(err instanceof OfflineError);
      }
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const signIn = useCallback(async (handle: string, password: string, remember: boolean) => {
    const signed = await login(handle, password, remember);
    generation.current += 1;
    setMe(signed);
    setOffline(false);
    noteSignedOut(false);
    // Whoever had this device before keeps nothing on it (`storedReads.ts`).
    await noteAccount(accountOf(signed));
    return signed;
  }, []);

  const signUp = useCallback(
    async (input: {
      handle: string;
      displayName: string;
      password: string;
      inviteCode: string;
    }) => {
      const made = await register(input);
      generation.current += 1;
      setMe(made);
      setOffline(false);
      noteSignedOut(false);
      await noteAccount(accountOf(made));
    },
    [],
  );

  // Whoever asked to leave has left this page, whatever the platform answered:
  // a logout that failed (a CSRF token rotated by React's doubled effect in
  // development, a network blip) used to leave the account signed in here, and
  // "Surt" landed back on the home page (the phone e2e, 2026-09-28).
  const signOut = useCallback(async () => {
    generation.current += 1;
    try {
      await logout();
    } finally {
      generation.current += 1;
      setMe({ ...ANONYMOUS });
      // Nothing the account read stays on the device once it has left
      // (roadmap item app-installable), the sign-out sent offline included;
      // a page with no connection says so (`SignedOutOffline`).
      noteSignedOut(true);
      await forgetAll();
    }
  }, []);

  return (
    <AuthContext.Provider value={{ me, loaded, offline, refresh, signIn, signOut, signUp }}>
      {children}
    </AuthContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthState {
  return useContext(AuthContext);
}
